import * as i0 from '@angular/core';
import { Injectable, Inject, ErrorHandler, NgModule } from '@angular/core';
import { timeout, take, combineLatest, filter, map, tap, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TimeoutError, ReplaySubject, throwError } from 'rxjs';
import * as i1$1 from '@angular/common/http';
import { HttpHeaders, HttpParams, HttpErrorResponse, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import * as i1 from '@angular/fire/compat/auth';
import * as i3 from '@angular/router';
import * as i4 from 'ngx-device-detector';
import { DeviceDetectorService } from 'ngx-device-detector';
import { DatePipe } from '@angular/common';
import { serverTimestamp } from 'firebase/database';
import * as i1$2 from '@angular/fire/compat/database';
import firebase from 'firebase/compat/app';
import { EndpointUrl } from '@ztarmobile/zwp-service';
import { BrowserModule } from '@angular/platform-browser';

class ErrorResponse {
}
class AuthHelperService {
    static getErrorMessage(response) {
        let errorJson;
        try {
            // This is fun, but believe me if you find another solution let me know
            errorJson = JSON.parse(JSON.parse(JSON.stringify(response)));
        }
        catch (ignored) {
            errorJson = response || {};
            if (response instanceof TimeoutError) {
                errorJson = { name: response?.name, message: 'Request timeout, please refresh and try again' };
            }
            else {
                errorJson = {
                    message: ((!!response && !!response?.statusText ? response?.statusText + ': ' : '') + (errorJson?.message || 'Request failed, please refresh and try again')),
                    name: !!response ? response?.name : 'request-failed'
                };
            }
        }
        if (!!response) {
            if (!!response?.url) {
                errorJson.url = errorJson?.url || response?.url;
            }
            if (!!response?.status) {
                errorJson.status = errorJson?.status || response?.status;
            }
            if (!!response?.code) {
                errorJson.code = errorJson?.code || response?.code;
            }
            const errorBody = response?.error;
            if (!!errorBody) {
                if (errorBody.backendMessage) {
                    errorJson.backendMessage = errorBody?.backendMessage;
                }
                if (errorBody.message) {
                    errorJson.message = errorBody?.message;
                }
            }
            else {
                if (!!response.backendMessage) {
                    errorJson.backendMessage = errorJson?.backendMessage || response?.backendMessage;
                }
                if (response?.message) {
                    errorJson.message = response?.message || errorJson?.backendMessage;
                }
            }
        }
        return errorJson;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AuthHelperService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AuthHelperService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AuthHelperService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class SimpleAuthService {
    constructor(angularFireAuth) {
        this.angularFireAuth = angularFireAuth;
        this.loginSubscriptionsReadySubject = new ReplaySubject(1);
        this.servicesIsReadySubject = new ReplaySubject(1);
        this.userStateSubject = new ReplaySubject(1);
        this.userCheckStateSubject = new ReplaySubject(1);
        this.subscriptions = [];
        this.dependentServices = {};
        this.subscribedServices = {};
        this.userState = this.userStateSubject.asObservable();
        this.userCheckState = this.userCheckStateSubject.asObservable();
        this.loginSubscriptionsReady = this.loginSubscriptionsReadySubject.asObservable();
        this.servicesIsReady = this.servicesIsReadySubject.asObservable();
        //this.auth = angularFireAuth.auth;
        this.auth = angularFireAuth;
        this.angularFireAuth.authState.subscribe((authState) => this.userCheckStateSubject.next(authState));
        this.loginSubscription = this.buildLoginSubscription();
    }
    registerDependentService(name, dependentService) {
        console.debug(`registering ${name}`);
        this.dependentServices[name] = dependentService;
        if (!!this.user) { // if already logged in, build immediately.
            this.onLoginSubscriptions(this.user);
        }
    }
    buildAllLoginSubscription() {
        return this.angularFireAuth.authState.subscribe((authState) => {
            this.servicesIsReadySubject.next(false);
            if (!!authState && !!authState.uid) {
                console.debug('login changed:', authState.email, '-', authState.uid);
                if (!this.user || (this.user.uid != authState.uid)) {
                    this.user = authState;
                    this.userStateSubject.next(authState);
                    this.onLoginSubscriptions(authState);
                    this.loginSubscriptionsReadySubject.next(authState);
                }
                else {
                    this.user = authState;
                }
            }
            else {
                console.debug('logged out *:', authState);
                this.user = undefined;
                this.userStateSubject.next(undefined);
                this.preLogoutCleanup();
            }
        });
    }
    buildSignupSubscription() {
        return this.angularFireAuth.authState.subscribe((authState) => {
            if (!!authState && !!authState.uid) {
                this.user = authState;
                this.userStateSubject.next(authState);
                this.onLoginSubscriptions(authState);
                this.loginSubscriptionsReadySubject.next(authState);
            }
            else {
                console.debug('logged out:', authState);
                this.user = undefined;
                this.userStateSubject.next(undefined);
                this.preLogoutCleanup();
            }
        });
    }
    unsubscribeAllServices() {
        console.log(`unsubscribe from ${this.subscriptions.length} services`);
        while (this.subscriptions.length > 0) {
            const subs = this.subscriptions.pop();
            console.log('unsubscribe from ' + subs.length + ' subscriptions');
            while (subs.length > 0) {
                const sub = subs.pop();
                sub.unsubscribe();
            }
        }
        this.subscribedServices = [];
    }
    preLogoutClean() {
        this.unsubscribeAll();
        this.onLogOutMethods();
        this.user = undefined;
        this.userStateSubject.next(undefined);
        this.userCheckStateSubject.next(undefined);
        this.servicesIsReadySubject.next(false);
    }
    buildLoginSubscription() {
        return this.angularFireAuth.authState.subscribe((authState) => {
            this.servicesIsReadySubject.next(false);
            if (!!authState && !!authState.uid) {
                console.debug('login changed:', authState.email, '-', authState.uid);
                if (!this.user || (this.user.uid != authState.uid)) {
                    this.user = authState;
                    this.userStateSubject.next(authState);
                    this.onLoginSubscriptions(authState);
                    this.loginSubscriptionsReadySubject.next(authState);
                }
                else {
                    this.user = authState;
                }
            }
            else {
                console.debug('logged out:', authState);
                this.user = undefined;
                this.userStateSubject.next(undefined);
                this.preLogoutCleanup();
            }
        });
    }
    preLogoutCleanup() {
        this.unsubscribeAll();
        this.onLogOutMethods();
        this.user = undefined;
        this.userStateSubject.next(undefined);
        this.userCheckStateSubject.next(undefined);
    }
    onLoginSubscriptions(fbUser) {
        Object.keys(this.dependentServices)
            .forEach((key) => {
            if (!this.subscribedServices[key]) {
                this.subscribedServices[key] = this.dependentServices[key];
                this.subscriptions.push(this.dependentServices[key].buildSubscriptions(fbUser));
            }
        });
        this.servicesIsReadySubject.next(true);
    }
    onLogOutMethods() {
        Object.keys(this.dependentServices)
            .forEach((key) => {
            this.dependentServices[key].onLoggedOut();
        });
        this.servicesIsReadySubject.next(true);
    }
    unsubscribeAll() {
        console.log(`unsubscribe from ${this.subscriptions.length} services`);
        while (this.subscriptions.length > 0) {
            const subs = this.subscriptions.pop();
            console.log('unsubscribe from ' + subs.length + ' subscriptions');
            while (subs.length > 0) {
                const sub = subs.pop();
                sub.unsubscribe();
            }
        }
        this.subscribedServices = [];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: SimpleAuthService, deps: [{ token: i1.AngularFireAuth }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: SimpleAuthService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: SimpleAuthService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.AngularFireAuth }] });

class AuthHttp {
    static { this.SERVER_TOKEN_TIMEOUT = 20000; }
    constructor(http, customErrorHandler, simpleAuth, router, deviceService) {
        this.http = http;
        this.customErrorHandler = customErrorHandler;
        this.simpleAuth = simpleAuth;
        this.router = router;
        this.deviceService = deviceService;
        this.FALLBACK_URL = 'login';
        this.serverTokenSubject = new ReplaySubject(1);
        this.useLanguageSubject = new ReplaySubject(1);
        this.unlocked = false;
        this.serverToken = this.serverTokenSubject.asObservable().pipe(timeout(AuthHttp.SERVER_TOKEN_TIMEOUT));
        this.useLanguage = this.useLanguageSubject.asObservable();
    }
    get(url, paramsObj) {
        const subject = new ReplaySubject(1);
        const params = this.getQueryParamsFromObject(paramsObj);
        this.getOptionsWithAuthenticationHeader().then((headers) => {
            this.http.get(url, { headers, params, withCredentials: true }).pipe(take(1)).subscribe((response) => {
                subject.next(response);
            }, (error) => {
                subject.error(error);
            });
        }, (error) => this.handleSubjectError(url, error, subject));
        return subject.asObservable();
    }
    getMedia(url) {
        const subject = new ReplaySubject(1);
        this.getOptionsWithAuthenticationHeader(null, 'image/webp,*/*', 'application/octet-stream').then((headers) => {
            this.http.get(url, { headers: headers, responseType: 'blob', withCredentials: true }).pipe(take(1)).subscribe((response) => {
                subject.next(response);
            }, (error) => {
                subject.error(error);
            });
        }, (error) => this.handleSubjectError(url, error, subject));
        return subject.asObservable();
    }
    getWithoutCredentials(url, paramsObj) {
        const subject = new ReplaySubject(1);
        const params = this.getQueryParamsFromObject(paramsObj);
        this.getOptionsWithAuthenticationHeader().then((headers) => {
            this.http.get(url, { headers, params, withCredentials: false }).pipe(take(1)).subscribe((response) => {
                subject.next(response);
            }, (error) => {
                subject.error(error);
            });
        }, (error) => this.handleSubjectError(url, error, subject));
        return subject.asObservable();
    }
    getWithOptions(url, paramsObj, extraHeaders) {
        const subject = new ReplaySubject(1);
        const params = this.getQueryParamsFromObject(paramsObj);
        this.getOptionsWithAuthenticationHeader(extraHeaders).then((headers) => {
            const options = Object.assign({ headers, params, withCredentials: true });
            this.http.get(url, options).pipe(take(1)).subscribe((response) => {
                subject.next(response);
            }, (error) => {
                subject.error(error);
            });
        }, (error) => this.handleSubjectError(url, error, subject));
        return subject.asObservable();
    }
    post(url, data) {
        const subject = new ReplaySubject(1);
        this.getOptionsWithAuthenticationHeader().then((headers) => {
            this.http.post(url, data, { headers, withCredentials: true }).pipe(take(1)).subscribe((response) => {
                subject.next(response);
            }, (error) => {
                subject.error(error);
            });
        }, (error) => this.handleSubjectError(url, error, subject));
        return subject.asObservable();
    }
    postWithoutCredentials(url, data) {
        const subject = new ReplaySubject(1);
        this.getOptionsWithAuthenticationHeader().then((headers) => {
            this.http.post(url, data, { headers, withCredentials: false }).pipe(take(1)).subscribe((response) => {
                subject.next(response);
            }, (error) => {
                subject.error(error);
            });
        }, (error) => this.handleSubjectError(url, error, subject));
        return subject.asObservable();
    }
    put(url, data, extraHeader) {
        const subject = new ReplaySubject(1);
        this.getOptionsWithAuthenticationHeader(extraHeader).then((headers) => {
            this.http.put(url, data || {}, { headers, withCredentials: true }).pipe(take(1)).subscribe((response) => {
                subject.next(response);
            }, (error) => {
                subject.error(error);
            });
        }, (error) => this.handleSubjectError(url, error, subject));
        return subject.asObservable();
    }
    putWithResponseText(url, data, extraHeader) {
        const subject = new ReplaySubject(1);
        this.getOptionsWithAuthenticationHeader(extraHeader).then((headers) => {
            this.http.put(url, data || {}, { headers, withCredentials: true, responseType: 'text' }).pipe(take(1)).subscribe((response) => {
                subject.next(response);
            }, (error) => {
                subject.error(error);
            });
        }, (error) => this.handleSubjectError(url, error, subject));
        return subject.asObservable();
    }
    patch(url, data, extraHeaders) {
        const subject = new ReplaySubject(1);
        if (!!extraHeaders) {
            this.getOptionsWithAuthenticationHeader(extraHeaders).then((headers) => {
                this.http.patch(url, data || {}, { headers, withCredentials: true }).pipe(take(1)).subscribe((response) => {
                    subject.next(response);
                }, (error) => {
                    subject.error(error);
                });
            }, (error) => this.handleSubjectError(url, error, subject));
        }
        else {
            this.getOptionsWithAuthenticationHeader().then((headers) => {
                this.http.patch(url, data || {}, { headers, withCredentials: true }).pipe(take(1)).subscribe((response) => {
                    subject.next(response);
                }, (error) => {
                    subject.error(error);
                });
            }, (error) => this.handleSubjectError(url, error, subject));
        }
        return subject.asObservable();
    }
    delete(url, paramsObj) {
        const subject = new ReplaySubject(1);
        const params = this.getQueryParamsFromObject(paramsObj);
        this.getOptionsWithAuthenticationHeader().then((headers) => {
            this.http.delete(url, { headers, params, withCredentials: true }).pipe(take(1)).subscribe((response) => subject.next(response), (error) => subject.error(error));
        }, (error) => this.handleSubjectError(url, error, subject));
        return subject.asObservable();
    }
    setLanguage(lang) {
        if (!!lang) {
            this.useLanguageSubject.next(lang);
        }
        else {
            this.useLanguageSubject.next(undefined);
        }
    }
    postWithHeader(url, data, extraHeaders) {
        const subject = new ReplaySubject(1);
        this.getOptionsWithAuthenticationHeader(extraHeaders).then((headers) => {
            this.http.post(url, data, { headers, withCredentials: true }).pipe(take(1)).subscribe((response) => {
                subject.next(response);
            }, (error) => {
                subject.error(error);
            });
        }, (error) => this.handleSubjectError(url, error, subject));
        return subject.asObservable();
    }
    postWithHeaderWithoutCredentials(url, data, extraHeaders) {
        const subject = new ReplaySubject(1);
        this.getOptionsWithAuthenticationHeader(extraHeaders).then((headers) => {
            this.http.post(url, data, { headers, withCredentials: false }).pipe(take(1)).subscribe((response) => {
                subject.next(response);
            }, (error) => {
                subject.error(error);
            });
        }, (error) => this.handleSubjectError(url, error, subject));
        return subject.asObservable();
    }
    hubspotPost(url, data) {
        const subject = new ReplaySubject(1);
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Access-Control-Allow-Origin', '*')
            .set('Accept', 'application/json');
        this.http.post(url, data, { headers }).pipe(take(1)).subscribe((response) => subject.next(response), (error) => subject.error(error));
        return subject.asObservable();
    }
    handleSubjectError(url, errorResponse, subject) {
        console.warn(`Request for ${url} failed`);
        if (errorResponse.error instanceof Error) {
            // A client-side or network error occurred. Handle it accordingly.
            console.warn(`SubjectError: An error occurred: ${errorResponse.error.message}`);
        }
        else {
            // The backend returned an unsuccessful response code.
            // The response body may contain clues as to what went wrong,
            console.warn(`SubjectError: Backend returned code ${errorResponse.status || 0}, body was: `, errorResponse.error || errorResponse);
        }
        const errorBody = AuthHelperService.getErrorMessage(errorResponse);
        subject.error(errorBody);
        this.customErrorHandler.handleError({ api: true, error: errorBody });
    }
    handlePromiseError(errorResponse, reject) {
        if (errorResponse.error instanceof Error) {
            // A client-side or network error occurred. Handle it accordingly.
            console.warn('PromiseError: An error occurred:', errorResponse.error.message);
        }
        else {
            // The backend returned an unsuccessful response code.
            // The response body may contain clues as to what went wrong,
            console.warn(`PromiseError: Backend returned code ${errorResponse.status || 0}, body was: `, errorResponse.error || errorResponse);
            this.handleRejectedServerToken(errorResponse?.error?.code === 466 ? errorResponse?.error : errorResponse);
        }
        console.warn('error code is ', errorResponse?.error?.code);
        const errorBody = AuthHelperService.getErrorMessage(errorResponse?.error?.code === 466 ? errorResponse?.error : errorResponse);
        reject(errorBody);
        this.customErrorHandler.handleError({ api: true, error: errorBody });
    }
    updateServerToken(token) {
        this.unlocked = true;
        this.serverTokenSubject.next(token);
    }
    handleRejectedServerToken(error, forceLogout = false) {
        if (!!error && !!error?.status) { // TODO maybe we need to logout? it is minor in case logout or not.
            // only requests with status 911 should logout (as token not found or expired), check BFF for this
            if (forceLogout || error?.status == 911) {
                this.simpleAuth.auth.signOut();
                this.router.navigate([this.FALLBACK_URL]);
            }
        }
    }
    postLogStash(data, requestMethod, logLevel, apiUrl) {
        let loggingObject = {};
        if (logLevel == 'error') {
            const errorObject = {
                createdAt: new Date().toDateString,
                location: window.location.href,
                datetime: new Date().toUTCString(),
                message: data.message || 'NO MESSAGE! maybe connection refused?',
                agent: this.deviceService.getDeviceInfo(),
                requestType: requestMethod,
                url: data.url,
                backendMessage: data.backendMessage
            };
            loggingObject = errorObject;
        }
        else {
            const dataObject = {
                createdAt: new Date().toUTCString(),
                location: window.location.href,
                agent: this.deviceService.getDeviceInfo(),
                requestType: requestMethod,
                requestDetails: data,
                url: apiUrl
            };
            loggingObject = dataObject;
        }
        const headers = new HttpHeaders()
            .set('Authorization', 'Basic ' + btoa('uiUser:L!Byw$aeZJKY&qhU'));
        return new Promise((resolve, reject) => {
            this.http.post(`https://34.67.109.234:9000`, loggingObject, { headers }).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.handlePromiseError(error, reject));
        });
    }
    getQueryParamsFromObject(paramsObj) {
        let queryString = '';
        if (!!paramsObj) {
            const keys = Object.keys(paramsObj);
            keys.forEach((key, index) => {
                queryString = queryString.concat(`${key}=${paramsObj[key]}`);
                if (keys.length > (index + 1)) {
                    queryString = queryString.concat('&');
                }
            });
        }
        return new HttpParams({ fromString: queryString });
    }
    getOptionsWithAuthenticationHeader(existHeaders, acceptType, contentType) {
        // This promise is to get headers only when the token is available (either with value or not)
        return new Promise((resolve, reject) => {
            // Combination to make sure if user logged in then we need server token first, we must not use the (empty) server token that is returned because it was set before we get
            // user logged in!
            // If user not logged in then it is ok to return empty token, this is why filtering data !!response[0] == !!response[1], both must exist or both must be empty
            this.simpleAuth.userState.pipe(combineLatest(this.serverToken, this.useLanguage))
                .pipe(filter((response) => !!response[0] == !!response[1]), map((response) => ({ token: response[1], lang: response[2] })))
                .subscribe((response) => {
                if (response.token === 'SERVER_DOWN') {
                    resolve(new HttpHeaders());
                }
                else {
                    const reCaptchaFound = !!existHeaders && !!existHeaders?.get('reCaptcha');
                    const vendorFound = !!existHeaders && !!existHeaders?.get('vendor');
                    let headers = new HttpHeaders()
                        .set('Content-Type', !!contentType ? contentType : 'application/json')
                        .set('Accept', !!acceptType ? acceptType : 'application/json')
                        .set('Authentication', !!response.token ? response.token : '')
                        .set('sec-fetch-dest', 'empty')
                        .set('sec-fetch-mode', 'cors')
                        .set('sec-fetch-site', 'cross-site');
                    if (reCaptchaFound) {
                        headers = headers.set('reCaptcha', existHeaders.get('reCaptcha'));
                    }
                    if (vendorFound) {
                        headers = headers.set('vendor', existHeaders.get('vendor'));
                    }
                    if (!!existHeaders && !!existHeaders?.get('action')) {
                        headers = headers.set('action', existHeaders.get('action'));
                    }
                    if (!!existHeaders && !!existHeaders?.get('invisibleRecaptcha')) {
                        headers = headers.set('invisibleRecaptcha', existHeaders.get('invisibleRecaptcha'));
                    }
                    if (!!response.lang) {
                        headers = headers.set('Accept-Language', response.lang);
                    }
                    resolve(headers);
                }
            }, (error) => reject(error));
        });
    }
    getHeadersWithHubspotRequest(existHeaders) {
        return new Promise((resolve, reject) => {
            // Combination to make sure if user logged in then we need server token first, we must not use the (empty) server token that is returned because it was set before we get
            // user logged in!
            // If user not logged in then it is ok to return empty token, this is why filtering data !!response[0] == !!response[1], both must exist or both must be empty
            this.simpleAuth.userState.pipe(combineLatest(this.serverToken))
                .pipe(filter((response) => !!response[0] == !!response[1]), map((response) => response[1])).pipe(take(1))
                .subscribe((token) => {
                if (token === 'SERVER_DOWN') {
                    resolve(new HttpHeaders());
                }
                else {
                    const reCaptchaFound = !!existHeaders && !!existHeaders.get('reCaptcha');
                    const headers = new HttpHeaders()
                        .set('Content-Type', 'application/json')
                        .set('Accept', 'application/json')
                        .set('Authentication', !!token ? token : '')
                        .set('reCaptcha', reCaptchaFound ? existHeaders.get('reCaptcha') : '');
                    resolve(headers);
                }
            }, (error) => reject(error));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AuthHttp, deps: [{ token: i1$1.HttpClient }, { token: i0.ErrorHandler }, { token: SimpleAuthService }, { token: i3.Router }, { token: i4.DeviceDetectorService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AuthHttp, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AuthHttp, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$1.HttpClient }, { type: i0.ErrorHandler }, { type: SimpleAuthService }, { type: i3.Router }, { type: i4.DeviceDetectorService }] });

class AngularInterceptor {
    static { this.REQUEST_TIMEOUT_IN_SECONDS = 180; }
    intercept(req, next) {
        return next.handle(req).pipe(timeout(AngularInterceptor.REQUEST_TIMEOUT_IN_SECONDS * 1000), tap((event) => {
            // console.debug(event);
        }, (err) => {
            if (err instanceof HttpErrorResponse) {
                console.log('Error Caught By Interceptor');
                throwError(err);
            }
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AngularInterceptor, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AngularInterceptor, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AngularInterceptor, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class ErrorHandlerService {
    constructor(angularFireDatabase, simpleAuthService, deviceService) {
        this.angularFireDatabase = angularFireDatabase;
        this.simpleAuthService = simpleAuthService;
        this.deviceService = deviceService;
        this.errors = new ReplaySubject(1);
        this.simpleAuthService.registerDependentService('custom-error-handler', this);
        if (!this.pushErrorSubscription) {
            this.pushErrorSubscription = this.errors.asObservable()
                .pipe(debounceTime(1000), distinctUntilChanged((report1, report2) => report1.error.message === report2.error.message))
                .subscribe((report) => this.angularFireDatabase.list(this.getErrorLoggingPath(report.isApi, report.dateString, this.userId))
                .push(report.error));
        }
    }
    handleError(error) {
        this.sendError(error.error || error, error.api);
    }
    buildSubscriptions(fbUser) {
        this.userId = fbUser.uid;
        return [];
    }
    onLoggedOut() {
        this.userId = undefined;
    }
    getErrorLoggingPath(apiCall, dateSting, userId) {
        return `/xLogs/${apiCall ? 'api' : 'client'}/date-${dateSting}/${userId || '-unknown'}`;
    }
    sendError(error, isApi) {
        const timeNow = new Date();
        const dateString = (new DatePipe('en-US')).transform(timeNow, 'yyyy-MM-dd');
        const errorObject = {
            createdAt: serverTimestamp(),
            location: window.location.href,
            datetime: timeNow.toUTCString(),
            message: error.message || 'NO MESSAGE! maybe connection refused?',
            agent: this.deviceService.getDeviceInfo()
        };
        if (!!error.url) {
            errorObject.url = error.url;
        }
        if (!!error.backendMessage) {
            errorObject.backendMessage = error.backendMessage;
        }
        console.warn('Sending the error: ', error);
        this.errors.next({ error: errorObject, isApi, dateString });
    }
    detectBrowser(userAgentString) {
        if (!userAgentString) {
            return null;
        }
        const browsers = [
            ['edge', /Edge\/([0-9\._]+)/],
            ['yandexbrowser', /YaBrowser\/([0-9\._]+)/],
            ['vivaldi', /Vivaldi\/([0-9\.]+)/],
            ['chrome', /(?!Chrom.*OPR)Chrom(?:e|ium)\/([0-9\.]+)(:?\s|$)/],
            ['crios', /CriOS\/([0-9\.]+)(:?\s|$)/],
            ['firefox', /Firefox\/([0-9\.]+)(?:\s|$)/],
            ['fxios', /FxiOS\/([0-9\.]+)/],
            ['opera', /Opera\/([0-9\.]+)(?:\s|$)/],
            ['opera', /OPR\/([0-9\.]+)(:?\s|$)$/],
            ['ie', /Trident\/7\.0.*rv\:([0-9\.]+).*\).*Gecko$/],
            ['ie', /MSIE\s([0-9\.]+);.*Trident\/[4-7].0/],
            ['ie', /MSIE\s(7\.0)/],
            ['bb10', /BB10;\sTouch.*Version\/([0-9\.]+)/],
            ['android', /Android\s([0-9\.]+)/],
            ['ios', /Version\/([0-9\._]+).*Mobile.*Safari.*/],
            ['safari', /Version\/([0-9\._]+).*Safari/]
        ];
        return browsers.map((rule) => {
            if (rule[1].test(userAgentString)) {
                const match = rule[1].exec(userAgentString);
                const version = match && match[1].split(/[._]/).slice(0, 3);
                if (version && version.length < 3) {
                    Array.prototype.push.apply(version, (version.length == 1) ? [0, 0] : [0]);
                }
                return {
                    name: rule[0],
                    version: version.join('.')
                };
            }
        }).filter(Boolean).shift();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ErrorHandlerService, deps: [{ token: i1$2.AngularFireDatabase }, { token: SimpleAuthService }, { token: i4.DeviceDetectorService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ErrorHandlerService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ErrorHandlerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$2.AngularFireDatabase }, { type: SimpleAuthService }, { type: i4.DeviceDetectorService }] });

class UserAuthService {
    constructor(endpointUrl, authHttp, httpClient, simpleAuth) {
        this.endpointUrl = endpointUrl;
        this.authHttp = authHttp;
        this.httpClient = httpClient;
        this.simpleAuth = simpleAuth;
        this.firebaseToken = new ReplaySubject(1);
        this.simpleAuth.registerDependentService('user-auth', this);
        // when we get a firebase token, send to server for a server token
        this.buildAuthSubscription();
        this.simpleAuth.userState.pipe(take(1)).subscribe((auth) => this.updateFirebaseToken(auth));
    }
    buildAuthSubscription() {
        if (!this.firebaseTokenObserver) {
            console.log('------buildAuthSubscription---------');
            this.firebaseTokenObserver = this.firebaseToken.pipe(debounceTime(150)).subscribe((firebaseToken) => this.updateServerToken(this.fbUser, firebaseToken), (error) => console.warn(error));
        }
    }
    buildSubscriptions(fbUser) {
        console.log('building subscriptions for user-auth.service');
        const subscriptions = [];
        // when auth-state is available, get a token and push it to authTokenSubject
        this.updateFirebaseToken(fbUser);
        return subscriptions;
    }
    onLoggedOut() {
        this.updateFirebaseToken(undefined);
    }
    login(email, password) {
        return new Promise((resolve, reject) => {
            this.firebaseLogin(email, password).then((user) => {
                resolve(user.user);
            }).catch((error) => {
                reject(error);
                this.logout();
            });
        });
    }
    angularAuthLogin(email, password) {
        return new Promise((resolve, reject) => {
            this.angularFireService.signOut().then(() => {
                this.angularFireService.signInWithEmailAndPassword(email, password).then((user) => {
                    resolve(user);
                }).catch((error) => {
                    reject(error);
                    this.logout();
                });
            });
        });
    }
    angularAuthSignup(reCaptchaResponse, newUser, password) {
        return new Promise((resolve, reject) => {
            return this.signUpWithBff(reCaptchaResponse, newUser, password).then((user) => {
                this.angularFireService.signInWithEmailAndPassword(user.email, user.password).then((user) => {
                    resolve(user);
                }).catch((error) => {
                    reject(error);
                    this.logout();
                });
            });
        });
    }
    /**
     *
     * @param {string} phoneNumber mdn
     * @param {ApplicationVerifier} applicationVerifier an instance of RecaptchaVerifier
     * @param {string} countryCode without + or 00 for USA it is 1 and this is the default value
     *                  https://en.wikipedia.org/wiki/E.164
     * @returns {Promise<ConfirmationResult>}
     */
    validateWithPhone(phoneNumber, applicationVerifier, countryCode) {
        // this should be link, it is not ready it is just for testing.
        return new Promise((resolve, reject) => {
            //  phone number should be E.164 format: https://en.wikipedia.org/wiki/E.164
            // Ref: https://angularfirebase.com/lessons/firebase-phone-authentication-with-angular-4-tutorial/
            // TODO currently we support USA only?
            this.firebasePhoneLink(`+${countryCode || '1'}${phoneNumber}`, applicationVerifier).then((confirmationResult) => {
                resolve(confirmationResult);
            }).catch((error) => {
                reject(error);
            });
        });
    }
    confirmWithPhone(verificationCode, confirmationResult) {
        // this should be link, it is not ready it is just for testing.
        return new Promise((resolve, reject) => {
            confirmationResult.confirm(verificationCode)
                .then((result) => {
                console.debug('Phone confirmed');
                this.firebasePhoneUnlink().then((response) => {
                    resolve(response);
                }, (error) => {
                    reject(error);
                });
            }).catch((error) => reject(error));
        });
    }
    /**
     * Force will logout old one to creat a new one
     * @param {boolean} force
     * @returns {Promise<FirebaseUser>}
     */
    loginAnonymous(force) {
        if (!!this.anonymousLoginPromise && !force) {
            return this.anonymousLoginPromise;
        }
        this.anonymousLoginPromise = new Promise((resolve, reject) => {
            if (force) {
                if (!!this.simpleAuth.auth.currentUser && this.simpleAuth.auth.currentUser.isAnonymous) {
                    this.firebaseRemoveNewUser();
                }
            }
            this.simpleAuth.auth.signInAnonymously().then((user) => {
                this.anonymousLoginPromise = null;
                resolve(user.user);
            }).catch((error) => {
                this.anonymousLoginPromise = null;
                reject(error);
                this.logout();
            });
        });
        return this.anonymousLoginPromise;
    }
    signUp(reCaptchaResponse, newUser, password) {
        const quickSignUp = !password;
        if (quickSignUp) {
            password = (Math.random() * 1e32).toString(36).slice(2);
            const displayName = newUser.fullName; // it is come with quick sign up method (email / name).
            if (!!displayName) {
                const name = displayName.split(' ');
                newUser.firstName = name[0];
                newUser.lastName = name[1] || '';
            }
        }
        return this.signUpUser(newUser, password, reCaptchaResponse);
    }
    bffSignUp(reCaptchaResponse, newUser, password) {
        const quickSignUp = !password;
        if (quickSignUp) {
            password = (Math.random() * 1e32).toString(36).slice(2);
            const displayName = newUser.fullName; // it is come with quick sign up method (email / name).
            if (!!displayName) {
                const name = displayName.split(' ');
                newUser.firstName = name[0];
                newUser.lastName = name[1] || '';
            }
        }
        return this.bffSignUpUser(newUser, password, reCaptchaResponse);
    }
    signUpWithBff(reCaptchaResponse, newUser, password, action, invisibleRecaptcha) {
        const quickSignUp = !password;
        if (!!quickSignUp) {
            password = (Math.random() * 1e32).toString(36).slice(2);
            const displayName = newUser.fullName; // it is come with quick sign up method (email / name).
            if (!!displayName) {
                const name = displayName.split(' ');
                newUser.firstName = name[0];
                newUser.lastName = name[1] || '';
            }
        }
        return this.signupUserWithBff(newUser, password, reCaptchaResponse, action, invisibleRecaptcha);
    }
    forgotPassword(email) {
        return this.firebaseForgotPassword(email);
    }
    changePassword(email, password, newPassword) {
        return new Promise((resolve, reject) => {
            this.firebaseChangePassword(email, password, newPassword).then((user) => {
                resolve(user);
            }).catch((error) => {
                reject(error);
            });
        });
    }
    bffChangePassword(email, password, newPassword) {
        return new Promise((resolve, reject) => {
            this.callBffChangePassword(email, password, newPassword).then(() => {
                this.firebaseLogin(email, newPassword).then((user) => {
                    resolve(user);
                });
            }).catch((error) => {
                reject(error);
            });
        });
    }
    changePasswordThroughBFF(email, password, newPassword) {
        return new Promise((resolve, reject) => {
            let updateParams;
            updateParams = new Map();
            updateParams['password'] = newPassword;
            this.authHttp.patch(this.endpointUrl + '/api/user/password', updateParams).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.authHttp.handlePromiseError(error, reject));
        });
    }
    setPasswordThroughBFF(email, password, verificationCode) {
        return this.callSetPasswordThroughBFF(email, password, verificationCode);
    }
    bffUpdateFirebaseProfile(newUser) {
        return new Promise((resolve, reject) => {
            this.callBffUpdateFirebaseProfile(newUser).then((res) => {
                resolve(res);
            }).catch((error) => {
                reject(error);
            });
        });
    }
    changeEmailAddress(email, password, newEmail) {
        return new Promise((resolve, reject) => {
            this.firebaseChangeEmail(email, password, newEmail).then((user) => {
                resolve(user);
            }).catch((error) => {
                reject(error);
            });
        });
    }
    bffChangeEmailAddress(email, password, newEmail) {
        return new Promise((resolve, reject) => {
            this.bffChangeEmail(email, password, newEmail).then((user) => {
                resolve(user);
            }).catch((error) => {
                reject(error);
            });
        });
    }
    verifyPasswordResetCode(oobCode) {
        return this.firebaseVerifyPasswordResetCode(oobCode);
    }
    confirmPasswordReset(oobCode, password) {
        return this.firebaseConfirmPasswordReset(oobCode, password);
    }
    sendEmailVerification() {
        return this.simpleAuth.auth.currentUser.sendEmailVerification();
    }
    logout() {
        return new Promise((resolve, reject) => {
            this.firebaseLogout().then(() => resolve(), (error) => reject(error));
        });
    }
    createHubspotContact(userData) {
        return new Promise((resolve, reject) => {
            this.hubspotSignUp(userData).then((res) => resolve(res), (error) => reject(error));
        });
    }
    getIpAddress() {
        return this.callGetIpAddress();
    }
    loginWithNewEmail(email, password) {
        return new Promise((resolve, reject) => {
            this.callLoginWithNewEmail(email, password).then((auth) => resolve(auth), (error) => reject(error));
        });
    }
    changeEmailThroughBFF(newEmail) {
        return new Promise((resolve, reject) => {
            let updateParams;
            updateParams = new Map();
            updateParams['email'] = newEmail;
            this.authHttp.patch(this.endpointUrl + '/api/user/email', updateParams).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.authHttp.handlePromiseError(error, reject));
        });
    }
    /**
     * Sends firebase token and user role from firebase user profile to the server.
     * server should generate a new token from firebase token and encrypt the user role in it.
     * @param {FirebaseUser} fbUser - the user
     * @param {string} firebaseToken - a firebase token
     */
    updateServerToken(fbUser, firebaseToken) {
        if (!!fbUser && !!fbUser.uid && !!firebaseToken) {
            this.httpClient.post(`${this.endpointUrl}/api/user/firebaseLogin`, {
                token: firebaseToken,
                oldToken: this.firebaseAnonymousToken
            }, { withCredentials: true })
                .pipe(take(1), map((x) => x.token)).subscribe((token) => {
                this.authHttp.updateServerToken(token);
                console.log('update server token');
            }, (error) => {
                this.handleServerTokenError(error);
            });
        }
        else {
            this.authHttp.updateServerToken(undefined);
        }
    }
    handleServerTokenError(error) {
        console.warn(!!error ? error.message : error);
        this.authHttp.updateServerToken(undefined);
        this.authHttp.handleRejectedServerToken(error, true);
    }
    signUpUser(newUser, password, reCaptchaResponse) {
        return new Promise((resolve, reject) => {
            this.simpleAuth.userState.pipe(take(1)).subscribe((auth) => {
                let promise;
                if (!!auth && !!this.simpleAuth.auth.currentUser) { // if true then user is Anonymous
                    promise = this.firebaseAuthLinkUser(newUser.email, password);
                }
                else {
                    promise = this.firebaseAuthCreateUser(newUser.email, password);
                }
                promise.then((firebaseUser) => {
                    newUser.id = firebaseUser.user.uid;
                    this.backendSignUp(reCaptchaResponse, newUser).then((newFirebaseUser) => {
                        this.login(newUser.email, password).then((user) => {
                            newUser.id = user.uid;
                            firebaseUser.user.sendEmailVerification();
                            resolve(newUser);
                        }).catch((error) => reject(error));
                    }, (error) => {
                        this.firebaseRemoveNewUser();
                        reject(error);
                    });
                }, (error) => reject(error));
            });
        });
    }
    bffSignUpUser(newUser, password, reCaptchaResponse) {
        return new Promise((resolve, reject) => {
            this.simpleAuth.userState.pipe(take(1)).subscribe((auth) => {
                if (!!auth && !!this.simpleAuth.auth.currentUser) {
                    newUser.id = this.simpleAuth.auth.currentUser.uid;
                }
                else {
                    newUser.id = null;
                }
                newUser.password = password;
                this.callBffSignUp(reCaptchaResponse, newUser).then((newFirebaseUser) => {
                    this.login(newUser.email, password).then((user) => {
                        newUser.id = user.uid;
                        this.simpleAuth.auth.currentUser.sendEmailVerification();
                        resolve(newUser);
                    }).catch((error) => reject(error));
                }, (error) => {
                    reject(error);
                });
            });
        });
    }
    signupUserWithBff(newUser, password, reCaptchaResponse, action, invisibleRecaptcha) {
        return new Promise((resolve, reject) => {
            newUser.password = password;
            this.callBffSignUp(reCaptchaResponse, newUser, action, invisibleRecaptcha).then((newFirebaseUser) => {
                resolve(newUser);
            }, (error) => {
                reject(error);
            });
        });
    }
    callBffSignUp(reCaptchaResponse, newUser, action, invisibleRecaptcha) {
        let header = new HttpHeaders()
            .set('reCaptcha', reCaptchaResponse);
        if (!!action) {
            header = header.set('action', action);
        }
        header = header.set('invisibleRecaptcha', `${!!invisibleRecaptcha}`);
        return new Promise((resolve, reject) => {
            newUser = Object.assign(newUser);
            this.httpClient.post(this.endpointUrl + '/api/user/firebaseSignUpNew', newUser, { withCredentials: true, headers: header }).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.authHttp.handlePromiseError(error, reject));
        });
    }
    updateFirebaseToken(fbUser) {
        console.log('------updateFirebaseToken---------');
        this.buildAuthSubscription();
        if (!!fbUser && !!fbUser.uid) {
            this.fbUser = fbUser;
            fbUser.getIdToken(true).then((firebaseToken) => {
                this.firebaseToken.next(firebaseToken);
                if (fbUser.isAnonymous) {
                    this.firebaseAnonymousToken = firebaseToken;
                }
            });
        }
        else {
            this.firebaseToken.next(undefined);
        }
    }
    backendSignUp(reCaptchaResponse, newUser) {
        return new Promise((resolve, reject) => {
            newUser = Object.assign(newUser, { reCaptcha: reCaptchaResponse });
            this.httpClient.post(this.endpointUrl + '/api/user/firebaseSignUp', newUser, { withCredentials: true }).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.authHttp.handlePromiseError(error, reject));
        });
    }
    hubspotSignUp(userData) {
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Access-Control-Allow-Origin', '*')
            .set('Accept', 'application/json');
        return new Promise((resolve, reject) => {
            this.httpClient.post('https://api.hsforms.com/submissions/v3/integration/submit/4922238/26a538cd-926d-4d0a-9c4d-804ac5c8657f', userData, { headers }).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.authHttp.handlePromiseError(error, reject));
        });
    }
    firebaseLogin(email, password) {
        return this.simpleAuth.auth.signInWithEmailAndPassword(email, password);
    }
    firebasePhoneLink(phoneNumber, applicationVerifier) {
        if (!!this.simpleAuth.auth.currentUser) {
            console.debug('Link phone for ', phoneNumber);
            return this.simpleAuth.auth.currentUser.linkWithPhoneNumber(phoneNumber, applicationVerifier);
        }
        else {
            this.loginAnonymous().then(() => {
                return this.firebasePhoneLink(phoneNumber, applicationVerifier);
            });
        }
    }
    firebasePhoneUnlink() {
        return this.simpleAuth.auth.currentUser.unlink(firebase.auth.PhoneAuthProvider.PROVIDER_ID);
    }
    firebaseRemoveNewUser() {
        return new Promise((resolve, reject) => {
            if (!!this.simpleAuth.auth.currentUser) {
                this.simpleAuth.auth.currentUser.delete().then((a) => {
                    this.firebaseLogout().then(() => resolve(), (error) => reject(error));
                }, (error) => reject(reject));
            }
            else {
                resolve();
            }
        });
    }
    firebaseAuthLinkUser(email, password) {
        return this.simpleAuth.auth.currentUser.linkWithCredential(firebase.auth.EmailAuthProvider.credential(email, password));
    }
    firebaseAuthCreateUser(email, password) {
        return this.simpleAuth.auth.createUserWithEmailAndPassword(email, password);
    }
    firebaseLogout() {
        return this.simpleAuth.auth.signOut();
    }
    firebaseForgotPassword(email) {
        return this.simpleAuth.auth.sendPasswordResetEmail(email);
    }
    firebaseChangePassword(email, password, newPassword) {
        return new Promise((resolve, reject) => {
            this.firebaseLogin(email, password).then((auth) => {
                auth.user.updatePassword(newPassword).then((response) => resolve(response), (error) => reject(error));
            }, (error) => reject(error));
        });
    }
    callBffChangePassword(email, password, newPassword) {
        return new Promise((resolve, reject) => {
            this.firebaseLogin(email, password).then((auth) => {
                let updateParams;
                updateParams = new Map();
                updateParams['password'] = newPassword;
                this.authHttp.patch(this.endpointUrl + '/api/user/password', updateParams).pipe(take(1))
                    .subscribe((response) => resolve(response), (error) => this.authHttp.handlePromiseError(error, reject));
            }, (error) => reject(error));
        });
    }
    callBffUpdateFirebaseProfile(newUser) {
        return new Promise((resolve, reject) => {
            this.authHttp.patch(this.endpointUrl + '/api/user/profile', newUser).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.authHttp.handlePromiseError(error, reject));
        });
    }
    firebaseChangeEmail(email, password, newEmail) {
        return new Promise((resolve, reject) => {
            this.firebaseLogin(email, password).then((auth) => {
                auth.user.updateEmail(newEmail).then((response) => resolve(response), (error) => reject(error));
            }, (error) => reject(error));
        });
    }
    bffChangeEmail(email, password, newEmail) {
        return new Promise((resolve, reject) => {
            this.firebaseLogin(email, password).then((auth) => {
                let updateParams;
                updateParams = new Map();
                updateParams['email'] = newEmail;
                this.authHttp.patch(this.endpointUrl + '/api/user/email', updateParams).pipe(take(1))
                    .subscribe((response) => resolve(response), (error) => this.authHttp.handlePromiseError(error, reject));
            }, (error) => reject(error));
        });
    }
    callLoginWithNewEmail(email, password) {
        return new Promise((resolve, reject) => {
            this.firebaseLogin(email, password).then((auth) => resolve(auth), (error) => reject(error));
        });
    }
    firebaseVerifyPasswordResetCode(oobCode) {
        return this.simpleAuth.auth.verifyPasswordResetCode(oobCode);
    }
    firebaseConfirmPasswordReset(oobCode, password) {
        return this.simpleAuth.auth.confirmPasswordReset(oobCode, password);
    }
    callGetIpAddress() {
        return new Promise((resolve, reject) => {
            this.httpClient.get(`https://freegeoip.net/json/?callback`).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.authHttp.handlePromiseError(error, reject));
        });
    }
    callSetPasswordThroughBFF(email, password, verificationCode) {
        const body = {
            email,
            password,
            verificationCode
        };
        return new Promise((resolve, reject) => {
            this.httpClient.patch(`${this.endpointUrl}/api/accounts/password`, body).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.authHttp.handlePromiseError(error, reject));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserAuthService, deps: [{ token: EndpointUrl }, { token: AuthHttp }, { token: i1$1.HttpClient }, { token: SimpleAuthService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserAuthService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserAuthService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: AuthHttp }, { type: i1$1.HttpClient }, { type: SimpleAuthService }] });

class AdminAuthService {
    constructor(endpointUrl, authHttp, simpleAuth) {
        this.endpointUrl = endpointUrl;
        this.authHttp = authHttp;
        this.simpleAuth = simpleAuth;
        this.adminSubject = new ReplaySubject(1);
        this.admin = this.adminSubject.asObservable();
        this.simpleAuth.registerDependentService('admin-auth', this);
        this.adminSubject.next(null);
    }
    buildSubscriptions(fbUser) {
        console.log('building subscriptions for admin-auth.service');
        const subscriptions = [];
        subscriptions.push(this.authHttp.serverToken.pipe(filter((token) => !!token), debounceTime(50)).subscribe(() => {
            this.authHttp.get(this.endpointUrl + '/api/user/isAdmin')
                .pipe(take(1)).subscribe((response) => {
                this.adminSubject.next(response);
            }, (error) => this.adminSubject.next(undefined));
        }));
        return subscriptions;
    }
    onLoggedOut() {
        this.adminSubject.next(undefined);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AdminAuthService, deps: [{ token: EndpointUrl }, { token: AuthHttp }, { token: SimpleAuthService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AdminAuthService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AdminAuthService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: AuthHttp }, { type: SimpleAuthService }] });

class AuthModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: AuthModule, imports: [BrowserModule,
            HttpClientModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AuthModule, providers: [
            { provide: HTTP_INTERCEPTORS, useClass: AngularInterceptor, multi: true },
            { provide: ErrorHandler, useClass: ErrorHandlerService },
            AuthHttp,
            SimpleAuthService,
            UserAuthService,
            AdminAuthService,
            AuthHelperService,
            DeviceDetectorService
        ], imports: [BrowserModule,
            HttpClientModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AuthModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        BrowserModule,
                        HttpClientModule,
                    ],
                    providers: [
                        { provide: HTTP_INTERCEPTORS, useClass: AngularInterceptor, multi: true },
                        { provide: ErrorHandler, useClass: ErrorHandlerService },
                        AuthHttp,
                        SimpleAuthService,
                        UserAuthService,
                        AdminAuthService,
                        AuthHelperService,
                        DeviceDetectorService
                    ]
                }]
        }] });

class UserData {
    constructor(initialUser) {
        this.createdAt = serverTimestamp();
        if (!!initialUser) {
            this.id = initialUser.uid;
            this.email = initialUser.email;
            this.verified = initialUser.emailVerified;
            //  this.hubspotDetails.ipAddress = '0.0.0.0';
        }
    }
    get fullName() {
        return this.firstName + ' ' + this.lastName;
    }
}

/*
 * Public API Surface of auth
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AdminAuthService, AngularInterceptor, AuthHelperService, AuthHttp, AuthModule, ErrorHandlerService, ErrorResponse, SimpleAuthService, UserAuthService, UserData };
